import React, { useState, useMemo, useEffect } from 'react';
import { Transaction, CoinListItem, Holding, PriceMap } from '../../types';
import { loadCoinImages } from '../../utils/coinImageCache';
import { TransactionForm } from './TransactionForm';
import { EditTransactionModal } from './EditTransactionModal';
import { TransactionsSidebar } from './TransactionsSidebar';
import { AllTransactionsTable } from './AllTransactionsTable';
import { CoinTransactionsView } from './CoinTransactionsView';
import { TransactionPagination } from './TransactionPagination';
import { WalletFilterSelect } from './WalletFilterSelect';
import { buildCoinTxnStats, buildWalletCoinTxnStats, txMatchesWallet } from './coinTxnStats';
import { buildDisplayRateTable } from '../../utils/displayRates';
import { useCurrency } from '../../context/CurrencyContext';
import { Receipt, PlusCircle, X, ArrowLeft, AlertTriangle } from 'lucide-react';
import { findUnfundedDisposals } from '../../utils/walletTimeline';

interface TransactionListProps {
  transactions: Transaction[];
  holdings?: Holding[];
  metals?: CoinListItem[];
  /** App-wide live prices (USD) — forwarded to the Add form's "Current" helper. */
  prices?: PriceMap;
  onAdd: (tx: Omit<Transaction, 'id' | 'timestamp'>) => void;
  onRemove: (id: string) => void;
  onEdit: (id: string, updates: Omit<Transaction, 'id' | 'timestamp'>) => void;
}

const PAGE_SIZE = 25;

export function TransactionList({ transactions, holdings = [], metals = [], prices = {}, onAdd, onRemove, onEdit }: TransactionListProps) {
  // `undefined` = no explicit choice yet → defaults to the first sidebar coin.
  // `null` = the user picked "All transactions". A coin id = that coin.
  const [selectedCoinId, setSelectedCoinId] = useState<string | null | undefined>(undefined);
  const [walletFilter,   setWalletFilter]   = useState('');
  const [page,           setPage]           = useState(0);
  const [editingTx,      setEditingTx]      = useState<Transaction | null>(null);
  const [showAddForm,    setShowAddForm]    = useState(false);
  // Mobile (< md) only: drill-in flag. false → show the list; true → show the
  // selected detail. Desktop ignores it (both panes always visible via md: classes).
  const [drilledIn,      setDrilledIn]      = useState(false);

  // Selecting a coin (or "All transactions") drills into the detail on mobile.
  const handleSelect = (coinId: string | null) => {
    setSelectedCoinId(coinId);
    setDrilledIn(true);
  };

  const { currency, rate } = useCurrency();

  const imageMap = useMemo(() => loadCoinImages(), []);

  // Per-coin sidebar stats (held + fully-sold positions), sorted by value desc.
  // The rate table is memoised and passed in so `invested` / `proceeds` /
  // `realizedPnl` are converted at each transaction's own date, matching the
  // holdings they sit next to.
  const displayRates = useMemo(
    () => buildDisplayRateTable(currency.code, rate),
    [currency.code, rate]
  );
  const coinStats = useMemo(
    () => buildCoinTxnStats(transactions, holdings, displayRates),
    [transactions, holdings, displayRates]
  );

  // The coin list as the wallet filter scopes it: only that wallet's coins,
  // with its counts and values. `coinStats` stays whole for the selected
  // coin's detail view, which filters its own rows.
  const sidebarCoins = useMemo(
    () => (walletFilter
      ? buildWalletCoinTxnStats(transactions, holdings, prices, walletFilter, displayRates)
      : coinStats),
    [walletFilter, transactions, holdings, prices, displayRates, coinStats]
  );

  // The selection actually applied: until the user chooses, default to the top
  // coin in the list; fall back to "All transactions" (null) if there are none.
  const effectiveSelectedId = selectedCoinId === undefined
    ? (sidebarCoins[0]?.coinId ?? null)
    : selectedCoinId;

  // Wallets used anywhere — drives the toolbar filter dropdown.
  const walletOptions = useMemo(() => {
    const ws = new Set<string>();
    transactions.forEach((tx) => {
      if (tx.wallet)     ws.add(tx.wallet);
      if (tx.fromWallet) ws.add(tx.fromWallet);
      if (tx.toWallet)   ws.add(tx.toWallet);
    });
    return Array.from(ws).sort();
  }, [transactions]);

  const matchesWallet = (tx: Transaction) => txMatchesWallet(tx, walletFilter);

  // If an explicitly-selected coin disappears (deleted its last txn), fall back to "All".
  const selectedStat = effectiveSelectedId ? coinStats.find((c) => c.coinId === effectiveSelectedId) ?? null : null;
  useEffect(() => {
    if (selectedCoinId && !coinStats.some((c) => c.coinId === selectedCoinId)) {
      setSelectedCoinId(null);
    }
  }, [selectedCoinId, coinStats]);

  // Reset to the first page whenever the view or wallet filter changes.
  useEffect(() => { setPage(0); }, [effectiveSelectedId, walletFilter]);

  // ── Derived lists for the active view ──────────────────────────────────────
  const sortedDesc = useMemo(
    () => [...transactions].sort((a, b) => b.timestamp - a.timestamp),
    [transactions]
  );

  const allFiltered = useMemo(() => sortedDesc.filter(matchesWallet), [sortedDesc, walletFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredCoinTxns = useMemo(
    () => (selectedStat ? sortedDesc.filter((t) => t.coinId === selectedStat.coinId && matchesWallet(t)) : []),
    [sortedDesc, selectedStat, walletFilter] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const activeList = selectedStat ? filteredCoinTxns : allFiltered;
  const safePage = Math.min(page, Math.max(0, Math.ceil(activeList.length / PAGE_SIZE) - 1));
  const pageSlice = activeList.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  /**
   * Sells and transfers the history cannot fund, across every coin and year.
   *
   * Shown to EVERY tier and worded without tax: an oversold wallet makes the
   * holdings, the average buy price and the P&L wrong, not just the CGT. Until
   * now the only signal lived behind the Pro gate on the Tax tab, and
   * computeHoldings clamps a negative balance to zero, so there was nothing on
   * screen anywhere to say the figures were off.
   */
  const unfunded = useMemo(() => findUnfundedDisposals(transactions), [transactions]);
  const unfundedCoins = useMemo(() => {
    const seen = new Map<string, { coinId: string; symbol: string }>();
    unfunded.forEach((u) => seen.set(u.coinId, { coinId: u.coinId, symbol: u.symbol }));
    return Array.from(seen.values());
  }, [unfunded]);

  const handleAdd = (tx: Omit<Transaction, 'id' | 'timestamp'>) => {
    onAdd(tx);
    setShowAddForm(false);
  };

  // ── Toolbar ────────────────────────────────────────────────────────────────
  const toolbar = (
    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
      <button
        type="button"
        onClick={() => setShowAddForm(true)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex-shrink-0"
        style={{ backgroundColor: '#d4b483', color: '#1a1410' }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#c9a972')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#d4b483')}
      >
        <PlusCircle className="w-4 h-4" />
        Add Transaction
      </button>

      {walletOptions.length > 0 && (
        <WalletFilterSelect value={walletFilter} options={walletOptions} onChange={setWalletFilter} />
      )}
    </div>
  );

  // ── Empty state (no transactions at all) ────────────────────────────────────
  if (transactions.length === 0) {
    return (
      <>
        {toolbar}
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Receipt className="w-6 h-6 mb-3" style={{ color: 'var(--color-sub)' }} />
          <p className="text-sm" style={{ color: 'rgb(var(--sl-100))' }}>No transactions yet</p>
          <p className="text-[12px] mt-1" style={{ color: 'var(--color-sub)' }}>Add a BUY, SELL or TRANSFER to get started</p>
        </div>
        {showAddForm && <AddTransactionModal onAdd={handleAdd} metals={metals} transactions={transactions} holdings={holdings} prices={prices} onClose={() => setShowAddForm(false)} />}
      </>
    );
  }

  return (
    <>
      {unfundedCoins.length > 0 && (
        <div
          className="flex items-start gap-2.5 p-3.5 rounded-xl mb-4"
          style={{ background: 'rgba(196,97,94,0.07)', border: '0.5px solid rgba(196,97,94,0.3)' }}
        >
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#c4615e' }} />
          <div className="min-w-0">
            <p className="text-[12px]" style={{ color: 'rgb(var(--sl-100))' }}>
              Some sells are more than you held at the time, so your figures may be off.
            </p>
            <p className="text-[11px] mt-1" style={{ color: 'var(--color-sub)' }}>
              {unfundedCoins.map((c, i) => (
                <React.Fragment key={c.coinId}>
                  {i > 0 && ' · '}
                  <button
                    type="button"
                    onClick={() => handleSelect(c.coinId)}
                    className="underline underline-offset-2"
                    style={{ color: '#b89968' }}
                  >
                    Review {c.symbol}
                  </button>
                </React.Fragment>
              ))}
            </p>
          </div>
        </div>
      )}

      {toolbar}

      {/* Mobile (< md): single column — the list OR the detail, toggled by
          `drilledIn` (both stay mounted; visibility via max-md: classes so the
          sidebar's search survives). Desktop (≥ md): the two-pane flex row. */}
      <div className="flex gap-4 items-start">
        <TransactionsSidebar
          coins={sidebarCoins}
          selectedCoinId={effectiveSelectedId}
          onSelect={handleSelect}
          totalCount={allFiltered.length}
          imageMap={imageMap}
          className={`max-md:!w-full ${drilledIn ? 'max-md:hidden' : ''}`}
        />

        <div
          className={`flex-1 min-w-0 ${drilledIn ? '' : 'max-md:hidden'}`}
          style={{
            background: 'rgb(var(--overlay) / 0.012)',
            border: '0.5px solid rgb(var(--overlay) / 0.05)',
            borderRadius: 12,
            padding: 16,
          }}
        >
          {/* Back to list — mobile only */}
          <button
            type="button"
            onClick={() => setDrilledIn(false)}
            className="md:hidden flex items-center gap-1.5 text-[13px] font-medium mb-3 transition-colors"
            style={{ color: 'var(--color-sub)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgb(var(--sl-100))'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-sub)'; }}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to list
          </button>

          {selectedStat ? (
            <CoinTransactionsView
              stat={selectedStat}
              filteredCoinTxns={filteredCoinTxns}
              pagedTxns={pageSlice}
              imageMap={imageMap}
              onEdit={setEditingTx}
              onDelete={onRemove}
            />
          ) : (
            <AllTransactionsTable
              rows={pageSlice}
              imageMap={imageMap}
              onEdit={setEditingTx}
              onDelete={onRemove}
            />
          )}

          <TransactionPagination
            page={safePage}
            totalItems={activeList.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      {editingTx && (
        <EditTransactionModal
          tx={editingTx}
          metals={metals}
          transactions={transactions}
          onSave={onEdit}
          onClose={() => setEditingTx(null)}
        />
      )}

      {showAddForm && (
        <AddTransactionModal
          onAdd={handleAdd}
          metals={metals}
          transactions={transactions}
          holdings={holdings}
          prices={prices}
          onClose={() => setShowAddForm(false)}
        />
      )}
    </>
  );
}

// ── Add Transaction modal (wraps the existing form, untouched) ─────────────────
// Exported so the dashboard empty state can open the same modal without
// duplicating the shell.

export function AddTransactionModal({ onAdd, metals, transactions, holdings, prices, onClose }: {
  onAdd: (tx: Omit<Transaction, 'id' | 'timestamp'>) => void;
  metals: CoinListItem[];
  transactions: Transaction[];
  holdings: Holding[];
  prices?: PriceMap;
  onClose: () => void;
}) {
  return (
    // Below md this is a full-height sheet rather than a centred card, measured
    // in dvh so a phone browser's address bar and toolbar cannot push the submit
    // button off the bottom (100vh ignores them and overflows by exactly their
    // height). The body scrolls inside a flex column so the sticky footer in the
    // form has a scroll container to pin itself to.
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 max-md:p-0 bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-950 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] relative flex flex-col max-md:rounded-none max-md:border-0 max-md:max-w-none max-md:h-[100dvh] max-md:max-h-[100dvh]">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex-1 min-h-0 overflow-y-auto p-6 max-md:px-4 max-md:pt-4 max-md:pb-0">
          <TransactionForm onAdd={onAdd} metals={metals} transactions={transactions} holdings={holdings} prices={prices} />
        </div>
      </div>
    </div>
  );
}
